import React, { useEffect, useMemo, useState } from 'react';
import RecommendationFilters from './RecommendationFilters';
import { matchesRecommendation, sortByEstimatedImpact, estimatedImpact } from '../../utils/performanceV2';
import RecCard from './RecCard';
import CampaignFilter from './CampaignFilter';
import { sortByPriority, cardInCampaign } from '../../utils/performanceCards';

/**
 * The card feed shared by Diagnostics, Budget & pacing, Moments and
 * Recommendations — the filter bar, the count line, the cards, and the
 * Dismissed section.
 *
 * Ported from the design project's _cards.js `draw()`. Three filters, in the
 * order the design puts them:
 *
 *   campaign     which campaign's cards to show — the searchable dropdown
 *                (CampaignFilter), one control across every tab
 *   can apply    only the ones with an executor behind them
 *   turn-on      reveal the cards hidden because enabling something is a build
 *                decision, not an optimisation
 *
 * Counts on the dropdown's options are computed over LIVE cards (dismissed ones
 * removed) and respect the turn-on toggle, so the number on an option always
 * equals what selecting it shows. An option that promises 5 and delivers 3 is
 * worse than no count.
 *
 * Dismissal is local to the session here. It moves the card into the Dismissed
 * section rather than deleting it — a dismissal is a judgement call, and the
 * section keeps it readable so the reader can see what they chose to ignore.
 */
export default function CardFeed({
  cards,
  performanceV2 = false,
  showCampaignFilter = true,
  recommendationFilters,
  onRecommendationFiltersChange,
  campaigns,          // [{ key, label }] — 'all' is prepended
  emptyState,
  orderedBy = 'priority',
  onApply,
  applying = {},          // { [cardId]: true } while that card's edit is in flight
  onDismiss,          // (card, {reason,note,scope,duration}) => Promise
  onReinstate,
  dismissedCards = [],   // suppressed server-side, shown with their reason
  appliedCards = [],     // applied through this product, shown with when and by whom
  reasonSets = {},
  notes = {},
  onCampaignChange,   // (resolvedKey) => void — the filter owns the whole tab
  footer = null,      // under the cards, above Applied/Dismissed (each tab's "More"); a function gets { onlyAuto }
}) {
  const [localFilters, setLocalFilters] = useState({ campaigns: [], platform: 'all', type: 'all', source: 'all' });
  const filters = recommendationFilters || localFilters;
  const setFilters = onRecommendationFiltersChange || setLocalFilters;
  const inScope = (c) => !performanceV2 || matchesRecommendation(c, filters, campaigns);
  const [camp, setCamp] = useState('all');
  const [sort, setSort] = useState(performanceV2 ? 'impact' : 'priority');
  const [onlyAuto, setOnlyAuto] = useState(false);
  const [showTurnOn, setShowTurnOn] = useState(false);
  // Optimistic: a card dismissed in this session leaves the feed immediately
  // rather than waiting for the refetch that confirms it.
  const [justDismissed, setJustDismissed] = useState({});
  // Likewise a card applied in this session: it moves to Applied the moment the
  // edit run is dispatched, and the server's own record takes over on reload.
  const [justApplied, setJustApplied] = useState({});

  // The stored key can outlive its option (the campaign's last card gets
  // dismissed) — resolve it to 'all' the same way CampaignFilter renders it,
  // so the feed and the control never disagree about what is selected.
  const campLive = camp !== 'all' && (campaigns || []).some((p) => p.key === camp)
    ? camp
    : 'all';

  // The control sits in the feed but scopes the WHOLE tab: the sections below it
  // (the delivery diagnosis above all) described the account while the feed
  // described one campaign, so narrowing to one campaign left 32 campaigns' worth
  // of diagnosis underneath it. Reported rather than lifted so every existing
  // caller keeps working without one. `campLive`, not `camp`, so a key that
  // resolved itself away is reported as what is actually shown.
  useEffect(() => { onCampaignChange?.(campLive); }, [campLive, onCampaignChange]);

  const live = useMemo(
    () => cards.filter((c) => !justDismissed[c.id] && !justApplied[c.id]),
    [cards, justDismissed, justApplied],
  );

  /** A card the current turn-on toggle admits. */
  const admitted = useMemo(
    () => live.filter((c) => showTurnOn || c.route !== 'hidden'),
    [live, showTurnOn],
  );

  const visible = useMemo(() => {
    const scoped = admitted.filter((c) => (performanceV2 ? matchesRecommendation(c, filters, campaigns) : campLive === 'all' || cardInCampaign(c, campLive)) && (!onlyAuto || c.auto));
    if (sort === 'impact') return sortByEstimatedImpact(scoped);
    const ranked = sortByPriority(scoped);
    return sort === 'campaign' ? ranked.sort((a, b) =>
      (a.campName || a.camp || '').localeCompare(b.campName || b.camp || '')
      || String(a.camp || '').localeCompare(String(b.camp || ''))) : ranked;
  }, [admitted, campLive, onlyAuto, performanceV2, filters, campaigns, sort]);

  const countFor = (key) => admitted.filter((c) => key === 'all' || cardInCampaign(c, key)).length;
  const autoN = admitted.filter((c) => c.auto && inScope(c)).length;
  const hiddenN = live.filter(
    (c) => c.route === 'hidden' && (performanceV2 ? inScope(c) : campLive === 'all' || cardInCampaign(c, campLive)),
  ).length;

  const options = useMemo(
    () => (campaigns || []).map((p) => ({ ...p, count: countFor(p.key) })),
    // countFor closes over `admitted`, which is what actually moves the counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaigns, admitted],
  );

  // Cards dismissed in this session, plus whatever the server already had.
  const sessionDismissed = cards.filter((c) => justDismissed[c.id] && inScope(c));
  const scopedDismissed = dismissedCards.filter(({ card }) => inScope(card));
  const scopedApplied = appliedCards.filter(inScope);
  const sessionApplied = cards.filter((c) => justApplied[c.id] && inScope(c));
  const appliedN = sessionApplied.length + scopedApplied.length;
  const scopeLabel = performanceV2 ? `across ${new Set(visible.map((c) => c.camp).filter(Boolean)).size} matching campaigns` : campLive === 'all'
    ? `across ${new Set(admitted.map((c) => c.camp).filter(Boolean)).size || 0} campaigns`
    : `on ${(campaigns || []).find((p) => p.key === campLive)?.label || campLive}`;

  return (
    <>
      <div className={visible.length && campLive !== 'all' ? 'one-camp' : undefined}>
        <div className="rc-bar2">
          {performanceV2 ? <RecommendationFilters showCampaignFilter={showCampaignFilter} cards={[...cards, ...appliedCards, ...dismissedCards.map((d) => d.card)]} countCards={admitted.filter((c) => !onlyAuto || c.auto)} campaigns={campaigns || []} value={filters} onChange={setFilters} /> : showCampaignFilter && <>
          <span className="rc-lbl">Campaign</span>
          <CampaignFilter
            value={campLive}
            onChange={setCamp}
            options={options}
            allCount={countFor('all')}
          /></>}
          <label className="rc-sort">Review order <select aria-label="Review order" value={sort} onChange={event => setSort(event.target.value)}>
            <option value="priority">Priority first</option>
            <option value="campaign">Campaign by campaign</option>
            {performanceV2 && <option value="impact">Estimated impact</option>}
          </select></label>
          <div className="rc-tog">
            <label>
              <input
                type="checkbox"
                checked={onlyAuto}
                onChange={(e) => setOnlyAuto(e.target.checked)}
              />
              {' '}Can apply <b>{autoN}</b>
            </label>
            {hiddenN ? (
              <label>
                <input
                  type="checkbox"
                  checked={showTurnOn}
                  onChange={(e) => setShowTurnOn(e.target.checked)}
                />
                {' '}Turn-on cards <b>{hiddenN}</b>
              </label>
            ) : null}
          </div>
        </div>

        <div className="rc-state">
          {visible.length
            ? (
              <>
                Showing <b>{visible.length}</b> {visible.length === 1 ? 'card' : 'cards'}
                {' '}{scopeLabel} · ordered by {sort === 'campaign' ? 'campaign, then priority' : sort === 'impact' ? 'estimated impact within comparable metrics; priority across metrics' : orderedBy}
              </>
            )
            : <>No cards {scopeLabel}</>}
        </div>

        {visible.length
          ? visible.map((c) => (
            <React.Fragment key={c.id}>
            {performanceV2 && <div className="perf-v2-impact">{estimatedImpact(c).label}</div>}
            <RecCard
              key={c.id}
              card={c}
              onApply={async (card) => {
                const out = await onApply?.(card);
                if (out?.ok) {
                  setJustApplied((a) => ({
                    ...a,
                    [card.id]: { status: 'applying', at: new Date().toISOString(), editMessageId: out.messageId || null },
                  }));
                }
                return out;
              }}
              applying={Boolean(applying[c.id])}
              reasonSets={reasonSets}
              notes={notes}
              onDismiss={async (card, payload) => {
                const rec = await onDismiss?.(card, payload);
                setJustDismissed((d) => ({ ...d, [card.id]: rec || true }));
                return rec;
              }}
            />
            </React.Fragment>
          ))
          : performanceV2 && cards.length ? <div className="rc-empty">No recommendations match these filters. Clear filters to see all recommendations.</div> : emptyState}
        {typeof footer === 'function' ? footer({ onlyAuto }) : footer}
      </div>

      {appliedN ? (
        <div className="rc-dsec rc-asec">
          <div className="sec-h">
            <span className="scope-tag camp">applied</span>
            <div className="sec-t">Applied</div>
            <div className="sec-n">
              {appliedN} {appliedN === 1 ? 'card' : 'cards'}
              {' '}&middot; applied through this product — kept for the record
            </div>
          </div>
          <div className="rc-dlist">
            {sessionApplied.map((c) => (
              <RecCard key={c.id} card={c} applied appliedEdit={justApplied[c.id]} />
            ))}
            {scopedApplied.map((c) => (
              <RecCard key={`a-${c.id}`} card={c} applied />
            ))}
          </div>
        </div>
      ) : null}

      {sessionDismissed.length || scopedDismissed.length ? (
        <div className="rc-dsec">
          <div className="sec-h">
            <span className="scope-tag camp">dismissed</span>
            <div className="sec-t">Dismissed</div>
            <div className="sec-n">
              {sessionDismissed.length + scopedDismissed.length}
              {' '}{sessionDismissed.length + scopedDismissed.length === 1 ? 'card' : 'cards'}
              {' '}&middot; each keeps the reason it was dismissed with
            </div>
          </div>
          <div className="rc-dlist">
            {sessionDismissed.map((c) => (
              <RecCard
                key={c.id}
                card={c}
                dismissed
                dismissal={typeof justDismissed[c.id] === 'object' ? justDismissed[c.id] : null}
                onReinstate={onReinstate}
              />
            ))}
            {scopedDismissed.map(({ card, dismissal }) => (
              <RecCard
                key={`d-${dismissal.id}`}
                card={card}
                dismissed
                dismissal={dismissal}
                onReinstate={onReinstate}
              />
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}
